/** Repair incomplete [[meet:id|title]] markers for assistant chat display. */

/** Titles like "." or "|" from truncated LLM tokens — never show these to users. */
export function isJunkMeetInlineTitle(rawTitle) {
  const t = String(rawTitle || "").replace(/[\[\]]/g, "").trim();
  if (!t) return true;
  if (/^[\]|.,;:\s]+$/u.test(t)) return true;
  if (t.length <= 2 && !/[A-Za-z0-9]/.test(t)) return true;
  return false;
}

/** "Team Team Team" / "Project UpdateProject Update" → single title. */
export function collapseStutteredMeetTitle(rawTitle) {
  let t = String(rawTitle || "").replace(/[\[\]]/g, "").trim();
  if (!t) return t;

  const words = t.split(/\s+/);
  if (words.length >= 2 && words.every((w) => w === words[0])) return words[0];

  if (t.length >= 4 && t.length % 2 === 0) {
    const half = t.slice(0, t.length / 2);
    if (half === t.slice(t.length / 2)) return half;
  }

  for (let len = 1; len <= Math.min(80, Math.floor(t.length / 2)); len += 1) {
    if (t.length % len !== 0) continue;
    const unit = t.slice(0, len);
    if (unit.repeat(t.length / len) === t) return unit;
  }

  return t.replace(/\s{2,}/g, " ").trim();
}

export function normalizeMeetInlineTitle(rawTitle) {
  return collapseStutteredMeetTitle(String(rawTitle || "").replace(/[\[\]]/g, "").trim());
}

export function meetingTitleFromCatalog(id, titleById, fallback = "Meeting") {
  const mid = String(id || "").trim();
  const rawTitle = normalizeMeetInlineTitle(fallback);
  if (!mid) return isJunkMeetInlineTitle(rawTitle) ? "Open meeting" : rawTitle || "Open meeting";
  const fromCat = titleById?.get(mid.toLowerCase());
  if (fromCat) return fromCat;
  if (!isJunkMeetInlineTitle(rawTitle) && rawTitle !== "Meeting" && rawTitle !== "Open meeting") {
    return rawTitle;
  }
  return "Open meeting";
}

function formatMeetToken(id, titleById, title) {
  const mid = String(id || "").trim();
  if (!mid) return "";
  const t = meetingTitleFromCatalog(mid, titleById, title);
  return `[[meet:${mid}|${t}]]`;
}

/** Break packed assistant answers into lines before block parsing. */
export function normalizeAssistantAnswerLayout(text) {
  let s = String(text || "");
  if (!s) return s;

  s = s.replace(/([.!?])\s*(\d{1,2}\.\s+(?:\[\[meet:|-\s*\*\*(?:Date|Summary|Participants):))/gi, "$1\n\n$2");
  s = s.replace(/(?<!\n)(\d{1,2}\.\s+\[\[meet:)/gi, "\n$1");
  s = s.replace(/(?<!\n)(\d{1,2}\.\s+-?\s*\*\*(?:Date|Summary|Participants):)/gi, "\n$1");
  s = s.replace(/\s*-\s*\*\*(Date|Summary|Participants):/gi, "\n- **$1:");
  s = s.replace(/(\[\[meet:[^\]|]+\|[^\]]+\]\])\s*(?:-\s*)?\*\*(Date|Summary|Participants):/gi, "$1\n- **$2:");
  s = s.replace(/(\[\[meet:[^\]|]+\|[^\]]+\]\])\s*(?:[A-Za-z0-9]\]\])+/gi, "$1");
  return s;
}

/** Shredded "Meeting]]" / "O]]O]]" tails — only inside assistant meet-link context. */
export function stripMeetDisplayArtifacts(text) {
  let s = String(text || "");
  if (!s) return s;
  s = s.replace(/(\[\[meet:[^\]|]+\|[^\]]+\]\])\s*(?:[A-Za-z0-9]\]\])+/gi, "$1");
  s = s.replace(/(?:Meeting\]\])+(?:[^\[]+?\]\])+/gi, "");
  s = s.replace(/(?:[A-Za-z0-9]\]\]){2,}/g, "");
  s = s.replace(/(\[\[meet:[^\]|]+\|[^\]]+\]\])([^\n]*)/g, (full, token, rest) => {
    let r = String(rest || "");
    r = r.replace(/(?:Meeting\]\])+/gi, "");
    const titleMatch = token.match(/\|([^\]]+)\]\]/i);
    if (titleMatch) {
      const title = titleMatch[1];
      const esc = title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      r = r.replace(new RegExp(`(?:${esc}\\]\\])+`, "gi"), "");
      r = r.replace(new RegExp(`^\\s*${esc}`, "i"), "");
    }
    r = r.replace(/(?:[A-Za-z0-9]\]\])+/g, "");
    r = r.replace(/\s*(?:\]\]){2,}\s*/g, " ");
    return token + r;
  });
  s = s.replace(/\]\]\s*Meeting(?=\s|$|[,.])/gi, "]]");
  return s;
}

export function isMeetDisplayTailJunk(str) {
  const t = String(str || "").trim();
  if (!t) return true;
  if (/^[\]\[\s.\-]+$/u.test(t)) return true;
  if (/^(?:[A-Za-z0-9]\]\])+\s*$/u.test(t.replace(/\s/g, ""))) return true;
  return false;
}

/** Remove duplicate titles and broken fragments after meet links. */
export function stripMeetLinkGarbage(text) {
  let s = String(text || "");
  if (!s) return s;

  s = s.replace(/(\[\[meet:[^\]|]+\|[^\]]+\]\])(?:\s*\1)+/gi, "$1");
  s = s.replace(/(\[\[meet:[^\]|]+\|([^\]]+)\]\])(?:\s*\2)+/gi, "$1");
  s = stripMeetDisplayArtifacts(s);
  return s;
}

export function repairAssistantMeetTokens(text, titleById) {
  let s = String(text || "");
  if (!s.includes("[[meet:") && !/gcal-/i.test(s)) return stripMeetLinkGarbage(s);

  s = s.replace(/\[\[meet:([^\]|]+)\|([^\]]+)\]\]/gi, (_, id, title) =>
    formatMeetToken(id, titleById, title)
  );
  s = s.replace(/\[\[meet:([^\]|]+)\]\]/gi, (_, id) => formatMeetToken(id, titleById, ""));

  s = s.replace(
    /(?:^|\n)(\s*(?:\d{1,2}\.\s*)?)((?:gcal-[a-z0-9._-]+|[0-9a-f-]{36})\|([^\]\n]+)\]\])/gi,
    (match, prefix, full, title) => {
      const id = String(full || "").split("|")[0].trim();
      const t = normalizeMeetInlineTitle(title);
      if (!id) return match;
      return `\n${prefix}${formatMeetToken(id, titleById, t)}`;
    }
  );
  s = s.replace(/(?:Meeting\]\])+(?=\[\[meet:)/gi, "");
  s = s.replace(/\[\[meet:([^\]|]+)\|(?:Meeting\]\])+/gi, "[[meet:$1|");
  s = s.replace(
    /(?:^|\n)(\s*(?:\d{1,2}\.\s*)?)\[\[meet:([0-9a-f-]{36})\s*(?=\n|$)/gim,
    (_, prefix, id) => `\n${prefix}${formatMeetToken(id, titleById, "")}`
  );

  s = s.replace(
    /\[\[meet:([0-9a-f-]{36}|gcal-[a-z0-9._-]+)\|[^\]\n]{0,240}(?!\]\])/gi,
    (chunk) => {
      const idM = chunk.match(/\[\[meet:([^\]|]+)/i);
      const mid = idM ? String(idM[1]).trim() : "";
      if (!mid) return "";
      const titlePart = chunk.replace(/\[\[meet:[^\]|]+\|/i, "");
      return formatMeetToken(mid, titleById, titlePart);
    }
  );

  return stripMeetLinkGarbage(s);
}
